import axios, { AxiosError } from "axios";
import { auth } from "../config/firebase";
import { FUNCTIONS } from "../config/api";
import {
  ApiResponse,
  ClaimCodeRequest,
  ClaimCodeResponse,
  RedeemValueRequest,
  RedeemValueResponse,
  WalletProjection,
  WalletItem,
  LedgerTransaction,
} from "../types";
import { collection, query, where, getDocs, getFirestore, doc, getDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";

/**
 * Get Firebase ID token for authenticated requests
 */
export async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }
  return await user.getIdToken(true);
}

/**
 * Create axios instance with Firebase ID token
 */
export async function createAuthHeader() {
  const token = await getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * Handle API errors with proper typing
 */
function handleApiError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ code: string; message: string }>;
    if (axiosError.response?.data) {
      throw new Error(`${axiosError.response.data.code}: ${axiosError.response.data.message}`);
    }
    throw new Error(`HTTP ${axiosError.status}: ${axiosError.message}`);
  }
  throw error;
}

/**
 * Redeem value from wallet item
 */
export async function redeemValue(
  walletItemId: string,
  amount: number,
  reason?: string
): Promise<RedeemValueResponse> {
  try {
    const headers = await createAuthHeader();
    const payload: RedeemValueRequest = {
      idempotencyKey: uuidv4(),
      walletItemId,
      amount,
      reason,
    };

    const response = await axios.post<ApiResponse<RedeemValueResponse>>(
      FUNCTIONS.WALLET_REDEEM,
      payload,
      { headers }
    );

    if (!response.data.success || !response.data.data) {
      throw new Error("Redeem failed: No data in response");
    }

    return response.data.data;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Claim giftcard by code
 */
export async function claimCode(
  code: string,
  externalAccountId?: string
): Promise<ClaimCodeResponse> {
  try {
    const headers = await createAuthHeader();
    const payload: ClaimCodeRequest = {
      idempotencyKey: uuidv4(),
      code,
      externalAccountId,
    };

    const response = await axios.post<ApiResponse<ClaimCodeResponse>>(
      FUNCTIONS.WALLET_CLAIM,
      payload,
      { headers }
    );

    if (!response.data.success || !response.data.data) {
      throw new Error("Claim failed: No data in response");
    }

    return response.data.data;
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Get wallet projection (balance + transactions) - Direct Firestore read
 */
export async function getWalletProjection(userId: string): Promise<WalletProjection> {
  try {
    const db = getFirestore();
    const projectionRef = doc(db, "wallet_projections", userId);
    const snapshot = await getDoc(projectionRef);

    if (!snapshot.exists()) {
      // Return empty projection if not found
      return {
        userId,
        totalGiftcardBalance: 0,
        totalRangeTokens: 0,
        greenfeeTickets: { "9_HOLES": 0, "18_HOLES": 0 },
        activeWalletItems: 0,
        recentTransactions: [],
        lastUpdatedAt: new Date(),
        ledgerVersion: 0,
      };
    }

    const data = snapshot.data();
    
    // Convert Firestore timestamps to Date objects
    return {
      userId: data.userId,
      totalGiftcardBalance: data.totalGiftcardBalance || 0,
      totalRangeTokens: data.totalRangeTokens || 0,
      greenfeeTickets: data.greenfeeTickets || { "9_HOLES": 0, "18_HOLES": 0 },
      activeWalletItems: data.activeWalletItems || 0,
      recentTransactions: (data.recentTransactions || []).map((tx: any) => ({
        txId: tx.transactionId || tx.txId,
        userId: tx.userId,
        type: tx.type,
        walletItemId: tx.walletItemId,
        amount: tx.amount,
        reason: tx.reason,
        status: tx.status,
        createdAt: tx.createdAt?.toDate ? tx.createdAt.toDate() : new Date(tx.createdAt),
        createdBy: tx.actedBy,
      } as LedgerTransaction)),
      lastUpdatedAt: data.lastUpdatedAt?.toDate ? data.lastUpdatedAt.toDate() : new Date(),
      ledgerVersion: data.ledgerVersion || 0,
    };
  } catch (error) {
    console.error("Failed to get wallet projection:", error);
    throw error;
  }
}

/**
 * Get all wallet items for a user
 */
export async function getWalletItems(userId: string): Promise<WalletItem[]> {
  try {
    const db = getFirestore();
    const walletItemsRef = collection(db, "wallet_items");
    const q = query(walletItemsRef, where("userId", "==", userId));
    const snapshot = await getDocs(q);
    
    const items: WalletItem[] = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        walletItemId: doc.id,
        userId: data.userId,
        type: data.type,
        value: data.value,
        usedAmount: data.usedAmount,
        count: data.quantity || data.count,
        status: data.status,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        expiresAt: data.expiresAt?.toDate ? data.expiresAt.toDate() : (data.expiresAt ? new Date(data.expiresAt) : undefined),
        claimedAt: data.claimedAt?.toDate ? data.claimedAt.toDate() : (data.claimedAt ? new Date(data.claimedAt) : undefined),
      } as WalletItem;
    });
    
    return items;
  } catch (error) {
    console.error("Failed to get wallet items:", error);
    throw error;
  }
}

/**
 * Check API health
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await axios.get<{ status: string; timestamp: string }>(FUNCTIONS.HEALTH_CHECK);
    return response.data.status === "ok";
  } catch {
    return false;
  }
}

/**
 * Setup mock data for testing
 * Populates Firestore with test users and wallet data
 */
export async function setupMockData(): Promise<any> {
  try {
    const setupMockDataFn = httpsCallable(functions, "setup_mock_data");
    const result = await setupMockDataFn({});
    return result.data;
  } catch (error) {
    console.error("Failed to setup mock data:", error);
    throw error;
  }
}
